// Phase 15 Plan 08 Task 3 — Rank-region 3-row panel.
//
// Renders the three explicit rows BELOW the top-100 cut when the caller is
// sub-top-100 AND the leaderboard response carries `slice_kind === 'near'`
// with a `rank_region` summary. Each row gets its own emoji glyph + i18n
// copy key (per 15-UI-SPEC §"Rank-region panel"):
//
//   🏅  Top 5%  — rank #{top_5_rank}
//   🥈  Top 25% — rank #{top_25_rank}
//   📊  You — rank #{your_rank} (median)   (Sun-Yellow 4px left edge)
//
// All values come straight from server-projected `rank_region` (DEC-021 —
// no client-side computation of percentiles / medians). When the caller IS
// in the top 100, the response's slice_kind stays 'top100' and the
// rank_region is omitted; this component returns null in that case.

import type { JSX } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'

import type { LeaderboardResponse } from '../../net/leaderboard'
import { Icon } from '../components/Icon'

export interface RankRegionPanelProps {
  readonly rankRegion: NonNullable<LeaderboardResponse['rank_region']> | null
}

interface Row {
  // Rank-award rows render a take-two medal line-icon tinted by tier; the
  // median ("you") row has no prototype glyph, so it keeps a text marker.
  readonly medalColor: string | null
  readonly glyph: string | null
  readonly messageId: string
  readonly defaultMessage: string
  readonly rank: number
  readonly testId: string
  readonly isYou: boolean
}

export function RankRegionPanel(
  props: RankRegionPanelProps,
): JSX.Element | null {
  const { rankRegion } = props
  // Rules-of-hooks: useIntl() before the !rankRegion early-return.
  const intl = useIntl()
  if (!rankRegion) return null

  const rows: ReadonlyArray<Row> = [
    {
      medalColor: 'var(--color-accent-gold)',
      glyph: null,
      messageId: 'social.leaderboard.region.top_5',
      defaultMessage: 'Top 5% — rank #{rank}',
      rank: rankRegion.top_5_rank,
      testId: 'rank-region-top-5',
      isYou: false,
    },
    {
      medalColor: 'var(--color-medal-silver)',
      glyph: null,
      messageId: 'social.leaderboard.region.top_25',
      defaultMessage: 'Top 25% — rank #{rank}',
      rank: rankRegion.top_25_rank,
      testId: 'rank-region-top-25',
      isYou: false,
    },
    {
      medalColor: null,
      glyph: '📊',
      messageId: 'social.leaderboard.region.median',
      defaultMessage: 'You — rank #{rank} (median)',
      rank: rankRegion.your_rank,
      testId: 'rank-region-median',
      isYou: true,
    },
  ]

  return (
    <section
      aria-label={intl.formatMessage({
        id: 'social.leaderboard.rank_region_aria',
      })}
      data-section="rank-region"
      data-testid="rank-region-panel"
      style={{
        width: '100%',
        maxWidth: '480px',
        padding: 'var(--space-2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      {rows.map((row) => (
        <div
          key={row.testId}
          data-testid={row.testId}
          className="sub-card"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3) var(--space-4)',
            minHeight: '48px',
            backgroundColor: 'var(--color-card)',
            borderRadius: '20px',
            boxShadow: row.isYou
              ? 'var(--shadow-card), 0 0 0 2.5px var(--color-player-a)'
              : 'var(--shadow-card)',
            color: 'var(--color-text-primary)',
          }}
        >
          {row.medalColor ? (
            <Icon name="medal" size={20} color={row.medalColor} />
          ) : (
            <span
              aria-hidden="true"
              style={{ fontSize: 'var(--font-size-20)' }}
            >
              {row.glyph}
            </span>
          )}
          <span
            style={{
              fontFamily: 'var(--font-family-body)',
              fontWeight: row.isYou ? 700 : 500,
              fontSize: 'var(--font-size-14)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <FormattedMessage
              id={row.messageId}
              defaultMessage={row.defaultMessage}
              values={{
                rank: <FormattedNumber value={row.rank} />,
              }}
            />
          </span>
        </div>
      ))}
    </section>
  )
}
